// src/hooks/useN8n.js
import { useState, useCallback, useEffect } from "react";
import { useToast } from "../context/ToastContext";
import { supabase } from "../lib/supabaseClient";
import {
  urlIngestWebhook,
  makeDraftWebhook,
  INGEST_BATCH_SIZE,
  INGEST_BATCH_DELAY_MS,
  LOG_LIMIT,
} from "../lib/constants";
import {
  parseUrlsWithReport,
  canonicalKey,
  chunk,
  sleep,
} from "../lib/utils";

export function useN8n(me, requests, loadRequests, contacts = []) {
  const { setToast } = useToast();

  const [webhookLog, setWebhookLog] = useState([]);
  const [urls, setUrls] = useState("");
  const [posting, setPosting] = useState(false);

  // Fetch webhook logs from database
  const fetchWebhookLogs = useCallback(async () => {
    if (!me?.org_id) return;

    try {
      const { data, error } = await supabase
        .from("webhook_logs")
        .select("*")
        .eq("org_id", me.org_id)
        .order("created_at", { ascending: false })
        .limit(LOG_LIMIT);

      if (error) {
        console.error("Error fetching webhook logs:", error);
        return;
      }

      // Transform database logs to match the format WebhookLogFeed expects
      const dbLogs = (data || []).map((log) => ({
        timestamp: log.created_at,
        workflow: log.event_type,
        data: log.payload,
      }));

      // Merge with any in-memory logs (from current session)
      setWebhookLog((prev) => {
        const inMemoryTimestamps = new Set(prev.map(l => l.timestamp));
        const newDbLogs = dbLogs.filter(l => !inMemoryTimestamps.has(l.timestamp));
        return [...prev, ...newDbLogs]
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, LOG_LIMIT);
      });
    } catch (err) {
      console.error("Error in fetchWebhookLogs:", err);
    }
  }, [me?.org_id]);

  // Fetch logs on mount and when org changes
  useEffect(() => {
    fetchWebhookLogs();
  }, [fetchWebhookLogs]);

  // Poll for new logs every 10 seconds
  useEffect(() => {
    const interval = setInterval(fetchWebhookLogs, 10000);
    return () => clearInterval(interval);
  }, [fetchWebhookLogs]);

  const postUrlsToN8n = useCallback(async () => {
    if (posting) return;
    const raw = String(urls || "").trim();
    if (!raw) {
      setToast({ type: "warn", msg: "Paste 1 or more URLs first." });
      return;
    }

    const parsed = parseUrlsWithReport(raw);
    const uniquePaste = parsed.unique;
    const dupesInPaste = parsed.dupesInPaste;
    const invalid = parsed.invalid;

    const have = new Set(
      (requests || [])
        .map((r) => r.article_url)
        .filter(Boolean)
        .map((u) => canonicalKey(u))
    );
    const alreadyInDb = uniquePaste.filter((u) => have.has(canonicalKey(u)));
    const toSend = uniquePaste.filter((u) => !have.has(canonicalKey(u)));

    if (toSend.length === 0) {
      setWebhookLog((prev) =>
        [
          {
            type: "ingest-analysis",
            at: Date.now(),
            timestamp: Date.now(), // Ensure timestamp is always present
            data: {
              message: "Nothing to send; all URLs were duplicates/invalid (DB or paste).",
              candidates: parsed.candidates,
              uniqueAfterPasteDedupe: uniquePaste.length,
              dupesInPaste,
              alreadyInDb,
              invalid,
              toSendCount: 0,
              batchCount: 0,
            },
          },
          ...prev,
        ].slice(0, LOG_LIMIT)
      );
      setToast({ type: "warn", msg: "No new URLs to send." });
      return;
    }

    setPosting(true);
    try {
      const batches = chunk(toSend, INGEST_BATCH_SIZE);

      setWebhookLog((prev) =>
        [
          {
            type: "ingest-analysis",
            at: Date.now(),
            timestamp: Date.now(), // Ensure timestamp is always present
            data: {
              candidates: parsed.candidates,
              uniqueAfterPasteDedupe: uniquePaste.length,
              dupesInPaste,
              alreadyInDb,
              invalid,
              toSendCount: toSend.length,
              sentUrls: toSend, // <-- Add this line
              batchCount: batches.length,
              batchSize: INGEST_BATCH_SIZE,
              delayMsBetweenBatches: INGEST_BATCH_DELAY_MS,
            },
          },
          ...prev,
        ].slice(0, LOG_LIMIT)
      );

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const payloadText = batch.join("\n");
        const idem = canonicalKey(batch[0]) + `|${batch.length}`;

        const resp = await fetch(urlIngestWebhook, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: payloadText,
            chatInput: payloadText,
            idempotency: idem,
            batchIndex: i,
            totalBatches: batches.length,
            orgId: me?.org_id || null,
            userId: me?.id || null,
          }),
        });

        const bodyText = await resp.text();
        let data;
        try {
          data = JSON.parse(bodyText);
        } catch {
          data = bodyText;
        }
        if (!resp.ok) {
          throw new Error(
            `n8n ingest failed (batch ${i + 1}/${batches.length}): ${
              resp.status
            } — ${String(bodyText).slice(0, 500)}`
          );
        }

        setWebhookLog((prev) =>
          [
            {
              type: "ingest-batch",
              at: Date.now(),
              timestamp: Date.now(), // Ensure timestamp is always present
              data: {
                batchIndex: i + 1,
                totalBatches: batches.length,
                count: batch.length,
                response: data,
              },
            },
            ...prev,
          ].slice(0, LOG_LIMIT)
        );

        if (i < batches.length - 1) await sleep(INGEST_BATCH_DELAY_MS);
      }

      setToast({
        type: "ok",
        msg: `Sent ${toSend.length} URL(s) in ${Math.ceil(
          toSend.length / INGEST_BATCH_SIZE
        )} batch(es).`,
      });
      setUrls("");
      setTimeout(loadRequests, 1500);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setPosting(false);
    }
  }, [posting, urls, requests, me?.org_id, me?.id, setToast, setWebhookLog, loadRequests]);

  const onMakeDraft = useCallback(async (row) => {
    let to = row.email_for_request || "";
    const agency = row.agency || "";
    const loc = row.location || "";
    const date = row.incident_date || "";
    const article = row.article_url || "";

    // If email_for_request is missing, try to extract from how_to_request
    if (!to || !String(to).trim()) {
      const howToRequest = row.how_to_request || "";
      const emailMatch = howToRequest.match(/[\w.-]+@[\w.-]+\.\w+/);
      if (emailMatch) {
        to = emailMatch[0];
      }
    }

    // If still missing, try to find it from contacts by agency
    if (!to || !String(to).trim()) {
      const matchedContact = contacts.find(c => c.agency && c.agency.toLowerCase() === agency.toLowerCase());
      if (matchedContact && matchedContact.email) {
        to = matchedContact.email;
      }
    }

    // Final guard: prevent sending drafts with an empty recipient
    if (!to || !String(to).trim()) {
      setToast({ type: "warn", msg: "No recipient email set — add an email to the agency contact or request." });
      return;
    }

    const subject = ["Public Records Request", agency, loc, date]
      .filter(Boolean)
      .join(" – ");

    const html =
      (row.draft_email && row.draft_email.replace(/\n/g, "<br>")) ||
      `<p>To the records custodian${agency ? " at " + agency : ""},</p>
<p>This is a public records request regarding an incident${date ? " on " + date : ""}${loc ? " in " + loc : ""}.</p>
${row.how_to_request ? `<p><strong>Notes:</strong> ${row.how_to_request}</p>` : ""}
${article ? `<p>Reference article: <a href="${article}">${article}</a></p>` : ""}
<p>Regards,<br>Jordy Kooij</p>`;

    try {
      const resp = await fetch(makeDraftWebhook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, subject, html, articleUrl: article }),
      });
      const text = await resp.text();
      let data;
      try { data = JSON.parse(text); } catch { data = text; }
      if (!resp.ok) throw new Error("n8n Draft failed");
      setWebhookLog((prev) =>
        [
          {
            type: "make-draft",
            at: Date.now(),
            timestamp: Date.now(), // Ensure timestamp is always present
            data,
          },
          ...prev,
        ].slice(0, LOG_LIMIT)
      );
      setToast({ type: "ok", msg: "Draft triggered." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setToast, setWebhookLog, contacts]);

  return {
    webhookLog,
    setWebhookLog,
    urls,
    setUrls,
    posting,
    postUrlsToN8n,
    onMakeDraft,
  };
}
